'use client';

import * as React from 'react';
import { Flag } from '@/components/ui/flag/flag';
import { Combobox, type ComboboxOption } from '@/components/ui/combobox/combobox';

function getFlagCode(countryCode: string): string {
  return countryCode.toUpperCase();
}

interface Country {
  code: string;
  name: string;
}

interface CountrySelectProps {
  countries: Country[];
  value?: string;
  onValueChange: (countryCode: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  className?: string;
  /** Class name for the popover dropdown content (useful for dark themes) */
  popoverClassName?: string;
}

/**
 * Country select component with flag rendering
 *
 * Wraps the Combobox component with country-specific rendering
 * that displays flags alongside country names.
 *
 * @example
 * ```tsx
 * <CountrySelect
 *   countries={[{ code: 'AU', name: 'Australia' }]}
 *   value={selectedCountry}
 *   onValueChange={setSelectedCountry}
 *   placeholder="Select a country..."
 * />
 * ```
 */
export function CountrySelect({
  countries,
  value,
  onValueChange,
  placeholder = 'Select a country...',
  searchPlaceholder = 'Search countries...',
  disabled = false,
  className,
  popoverClassName,
}: CountrySelectProps) {
  const options: ComboboxOption[] = React.useMemo(
    () =>
      countries.map((country) => ({
        value: country.code,
        label: country.name,
      })),
    [countries],
  );

  const renderCountryOption = (option: ComboboxOption) => {
    return (
      <span className="flex items-center gap-2">
        <Flag code={getFlagCode(option.value)} size="sm" />
        <span className="truncate">{option.label}</span>
      </span>
    );
  };

  const renderCountryValue = (option: ComboboxOption) => {
    return (
      <span className="flex items-center gap-2">
        <Flag code={getFlagCode(option.value)} size="sm" />
        <span className="truncate">{option.label}</span>
      </span>
    );
  };

  return (
    <Combobox
      options={options}
      value={value}
      onValueChange={onValueChange}
      placeholder={placeholder}
      searchPlaceholder={searchPlaceholder}
      emptyMessage="No countries found."
      disabled={disabled}
      className={className}
      popoverClassName={popoverClassName}
      renderOption={renderCountryOption}
      renderValue={renderCountryValue}
    />
  );
}

export type { Country, CountrySelectProps };
